import { useState } from 'react';

interface Friend {
  id: string;
  name: string;
  avatar: string;
  level: number;
  xp: number;
  badges: number;
  missions: number;
  rank: number;
  isOnline?: boolean;
  lastActive?: string;
  mutualFriends?: number;
  status?: 'confirmed' | 'pending' | 'suggested';
}

interface ComparisonChartProps {
  confirmedFriends?: Friend[];
  data?: {
    you: Friend;
    friend: Friend;
  };
}

export function ComparisonChart({ confirmedFriends = [], data }: ComparisonChartProps) {
  const [selectedFriend, setSelectedFriend] = useState<Friend | null>(
    confirmedFriends.length > 0 ? confirmedFriends[0] : null
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Se data foi passada, usar ela diretamente
  if (data) {
    const currentUser = {
      name: data.you.name,
      level: data.you.level,
      xp: data.you.xp,
      badges: data.you.badges,
      missions: data.you.missions,
      ranking: data.you.rank,
    };

    const friendStats = {
      name: data.friend.name,
      level: data.friend.level,
      xp: data.friend.xp,
      badges: data.friend.badges,
      missions: data.friend.missions,
      ranking: data.friend.rank,
    };

    const metrics = [
      { label: 'Nível', key: 'level' as const, icon: 'ri-bar-chart-line', color: 'teal' },
      { label: 'XP Total', key: 'xp' as const, icon: 'ri-star-line', color: 'purple' },
      { label: 'Distintivos', key: 'badges' as const, icon: 'ri-medal-line', color: 'orange' },
      { label: 'Missões', key: 'missions' as const, icon: 'ri-flag-line', color: 'blue' },
      { label: 'Ranking', key: 'ranking' as const, icon: 'ri-trophy-line', color: 'yellow', inverse: true },
    ];

    const getPercentage = (userValue: number, friendValue: number, max: number) => {
      return (userValue / max) * 100;
    };

    const getMaxValue = (key: string) => {
      const userVal = currentUser[key as keyof typeof currentUser] as number;
      const friendVal = friendStats[key as keyof typeof friendStats] as number;
      return Math.max(userVal, friendVal) * 1.2;
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
            <i className="ri-bar-chart-grouped-line text-2xl text-white"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Compare seu Progresso</h2>
            <p className="text-gray-600">Veja como você está em relação ao seu amigo</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Cards de Comparação */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Você */}
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{currentUser.name}</h3>
                  <p className="text-white/80 text-sm">Suas Estatísticas</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-3xl font-bold">{currentUser.level}</div>
                  <div className="text-white/80 text-sm">Nível</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{currentUser.xp.toLocaleString()}</div>
                  <div className="text-white/80 text-sm">XP</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{currentUser.badges}</div>
                  <div className="text-white/80 text-sm">Distintivos</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">#{currentUser.ranking}</div>
                  <div className="text-white/80 text-sm">Ranking</div>
                </div>
              </div>
            </div>

            {/* Amigo */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={data.friend.avatar}
                  alt={data.friend.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{friendStats.name}</h3>
                  <p className="text-gray-500 text-sm">Estatísticas do Amigo</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-3xl font-bold text-gray-900">{friendStats.level}</div>
                  <div className="text-gray-500 text-sm">Nível</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{friendStats.xp.toLocaleString()}</div>
                  <div className="text-gray-500 text-sm">XP</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{friendStats.badges}</div>
                  <div className="text-gray-500 text-sm">Distintivos</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">#{friendStats.ranking}</div>
                  <div className="text-gray-500 text-sm">Ranking</div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos de Comparação */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Comparação Detalhada</h3>
            <div className="space-y-6">
              {metrics.map((metric) => {
                const userValue = currentUser[metric.key];
                const friendValue = friendStats[metric.key];
                const maxValue = getMaxValue(metric.key);
                const userPercentage = getPercentage(userValue, friendValue, maxValue);
                const friendPercentage = getPercentage(friendValue, userValue, maxValue);
                const userAhead = metric.inverse ? userValue < friendValue : userValue > friendValue;

                return (
                  <div key={metric.key}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <i className={`${metric.icon} text-${metric.color}-600`}></i>
                        <span className="font-medium text-gray-900">{metric.label}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {userAhead ? (
                          <span className="text-teal-600 font-medium">
                            Você está na frente por {Math.abs(userValue - friendValue)}
                          </span>
                        ) : userValue === friendValue ? (
                          <span className="text-gray-600 font-medium">Empate!</span>
                        ) : (
                          <span className="text-orange-600 font-medium">
                            Atrás por {Math.abs(userValue - friendValue)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {/* Você */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-20">Você</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                            style={{ width: `${userPercentage}%` }}
                          >
                            <span className="text-white text-sm font-bold">{userValue.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      {/* Amigo */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-20 truncate">{friendStats.name}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-gray-300 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                            style={{ width: `${friendPercentage}%` }}
                          >
                            <span className="text-gray-700 text-sm font-bold">{friendValue.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modo com lista de amigos confirmados
  const currentUser = {
    name: 'Você',
    level: 11,
    xp: 7890,
    badges: 21,
    missions: 45,
    ranking: 8,
  };

  const friendStats = selectedFriend
    ? {
        name: selectedFriend.name,
        level: selectedFriend.level,
        xp: selectedFriend.xp,
        badges: selectedFriend.badges,
        missions: selectedFriend.missions,
        ranking: selectedFriend.rank,
      }
    : null;

  const metrics = [
    { label: 'Nível', key: 'level' as const, icon: 'ri-bar-chart-line', color: 'teal' },
    { label: 'XP Total', key: 'xp' as const, icon: 'ri-star-line', color: 'purple' },
    { label: 'Distintivos', key: 'badges' as const, icon: 'ri-medal-line', color: 'orange' },
    { label: 'Missões', key: 'missions' as const, icon: 'ri-flag-line', color: 'blue' },
    { label: 'Ranking', key: 'ranking' as const, icon: 'ri-trophy-line', color: 'yellow', inverse: true },
  ];

  const getPercentage = (userValue: number, friendValue: number, max: number) => {
    return (userValue / max) * 100;
  };

  const getMaxValue = (key: string) => {
    if (!friendStats) return 100;
    const userVal = currentUser[key as keyof typeof currentUser] as number;
    const friendVal = friendStats[key as keyof typeof friendStats] as number;
    return Math.max(userVal, friendVal) * 1.2;
  };

  // Filtrar amigos confirmados pela busca
  const filteredFriends = confirmedFriends.filter(friend =>
    friend.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
          <i className="ri-bar-chart-grouped-line text-2xl text-white"></i>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Compare seu Progresso</h2>
          <p className="text-gray-600">Veja como você está em relação aos seus amigos</p>
        </div>
      </div>

      {confirmedFriends.length > 0 ? (
        <div className="space-y-6">
          {/* Busca e Seletor de Amigo */}
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <i className="ri-search-line mr-2"></i>
              Buscar e selecionar amigo para comparar:
            </label>
            
            {/* Campo de Busca */}
            <div className="relative mb-3">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Digite o nome do amigo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Seletor de Amigo */}
            {filteredFriends.length > 0 ? (
              <select
                value={selectedFriend?.id || ''}
                onChange={(e) => {
                  const friend = confirmedFriends.find((f) => f.id === e.target.value);
                  setSelectedFriend(friend || null);
                }}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer bg-white"
              >
                {filteredFriends.map((friend) => (
                  <option key={friend.id} value={friend.id}>
                    {friend.name} - Nível {friend.level} ({friend.xp.toLocaleString()} XP)
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                <i className="ri-user-search-line text-2xl mb-2"></i>
                <p>Nenhum amigo encontrado com esse nome</p>
              </div>
            )}

            {filteredFriends.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                <i className="ri-information-line mr-1"></i>
                Mostrando {filteredFriends.length} de {confirmedFriends.length} amigo{confirmedFriends.length !== 1 ? 's' : ''} confirmado{confirmedFriends.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {friendStats && selectedFriend && (
            <>
              {/* Cards de Comparação */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Você */}
                <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{currentUser.name}</h3>
                      <p className="text-white/80 text-sm">Suas Estatísticas</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold">{currentUser.level}</div>
                      <div className="text-white/80 text-sm">Nível</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">{currentUser.xp.toLocaleString()}</div>
                      <div className="text-white/80 text-sm">XP</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">{currentUser.badges}</div>
                      <div className="text-white/80 text-sm">Distintivos</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">#{currentUser.ranking}</div>
                      <div className="text-white/80 text-sm">Ranking</div>
                    </div>
                  </div>
                </div>

                {/* Amigo */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={selectedFriend.avatar}
                      alt={selectedFriend.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{friendStats.name}</h3>
                      <p className="text-gray-500 text-sm">Estatísticas do Amigo</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-gray-900">{friendStats.level}</div>
                      <div className="text-gray-500 text-sm">Nível</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">{friendStats.xp.toLocaleString()}</div>
                      <div className="text-gray-500 text-sm">XP</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">{friendStats.badges}</div>
                      <div className="text-gray-500 text-sm">Distintivos</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">#{friendStats.ranking}</div>
                      <div className="text-gray-500 text-sm">Ranking</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráficos de Comparação */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Comparação Detalhada</h3>
                <div className="space-y-6">
                  {metrics.map((metric) => {
                    const userValue = currentUser[metric.key];
                    const friendValue = friendStats[metric.key];
                    const maxValue = getMaxValue(metric.key);
                    const userPercentage = getPercentage(userValue, friendValue, maxValue);
                    const friendPercentage = getPercentage(friendValue, userValue, maxValue);
                    const userAhead = metric.inverse ? userValue < friendValue : userValue > friendValue;

                    return (
                      <div key={metric.key}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <i className={`${metric.icon} text-${metric.color}-600`}></i>
                            <span className="font-medium text-gray-900">{metric.label}</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {userAhead ? (
                              <span className="text-teal-600 font-medium">
                                Você está na frente por {Math.abs(userValue - friendValue)}
                              </span>
                            ) : userValue === friendValue ? (
                              <span className="text-gray-600 font-medium">Empate!</span>
                            ) : (
                              <span className="text-orange-600 font-medium">
                                Atrás por {Math.abs(userValue - friendValue)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {/* Você */}
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 w-20">Você</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                                style={{ width: `${userPercentage}%` }}
                              >
                                <span className="text-white text-sm font-bold">{userValue.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                          {/* Amigo */}
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 w-20 truncate">{friendStats.name}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-gray-300 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                                style={{ width: `${friendPercentage}%` }}
                              >
                                <span className="text-gray-700 text-sm font-bold">{friendValue.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desafios Sugeridos */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-sword-line text-orange-600"></i>
                  Desafios Sugeridos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <div className="text-2xl mb-2">🏆</div>
                    <h4 className="font-semibold text-gray-900 mb-1">Corrida de XP</h4>
                    <p className="text-sm text-gray-600">Quem ganha mais XP em 7 dias</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <div className="text-2xl mb-2">🎯</div>
                    <h4 className="font-semibold text-gray-900 mb-1">Maratona de Missões</h4>
                    <p className="text-sm text-gray-600">Complete mais missões que seu amigo</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <div className="text-2xl mb-2">🏅</div>
                    <h4 className="font-semibold text-gray-900 mb-1">Caça aos Distintivos</h4>
                    <p className="text-sm text-gray-600">Desbloqueie distintivos raros primeiro</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-user-add-line text-4xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum amigo confirmado ainda</h3>
          <p className="text-gray-600 mb-6">Adicione e confirme amizades para comparar seu progresso</p>
        </div>
      )}
    </div>
  );
}
